import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';

import {Button, CloseIcon, Divider, IconButton, Layout, ListItem, ReloadIcon, SearchIcon, Select, Text} from '@phonectas/unifon-ui-kit';
import Events from './components/Events';
import SearchWidget from 'common/components/SearchWidget';
import Portal from 'common/components/Portal';
import CommentEvent from './components/CommentEvent';
import {StyledGrowLayout} from './styled';
import {LIMIT_PAYLOAD} from 'common/constants';

interface CustomerEventsProps {
	customerId?: string;
}

const CustomerEvents: React.FC<CustomerEventsProps> = ({customerId}) => {
	const [t] = useTranslation();
	const {
		getAccountFeed,
		getUserConfig,
		updatePageFilters,
	} = useThreeSixtyNewStore((state) => ({
		getAccountFeed: state.getAccountFeed,
		getUserConfig: state.getUserConfig,
		updatePageFilters: state.updatePageFilters,
	}), shallow);
	const {
		accountFeed,
		activeAccount,
		customFilters,
		userConfig,
		// pageFilters,
	} = useThreeSixtyNewStore((state) => ({
		accountFeed: state?.accountFeed,
		activeAccount: state?.activeAccount,
		customFilters: state?.userConfig?.configs?.account_feed_filter,
		userConfig: state?.userConfig?.configs,
		pageFilters: state?.pageFilters?.eventFeed,
	}), shallow);
	const [filter, setFilter] = useState<string[]>([]);
	const [currentLimit, setCurrentLimit] = useState(LIMIT_PAYLOAD);
	const [searchQuery, setSearchQuery] = useState('');
	const [showSearch, setShowSearch] = useState(false);
	const hasFetched = useRef(false);
	// A ref, so scroll events fired before the next render still see the running fetch
	const isFetchingRef = useRef(false);

	// SearchWidget debounces, and sends '' when it opens and when the field is emptied
	const handleSearch = useCallback(
		(query: string) => {
			if (query === searchQuery) return;
			setCurrentLimit(LIMIT_PAYLOAD);
			setSearchQuery(query);
			if (activeAccount?.id) {
				getAccountFeed({
					id: activeAccount.id,
					limit: LIMIT_PAYLOAD,
					search: query,
					feedType: filter,
				});
			}
		},
		[activeAccount?.id, getAccountFeed, filter, searchQuery]
	);

	const resetFilters = useCallback(() => {
		setFilter(customFilters ?? []);
		updatePageFilters(({eventFeed: customFilters || []}));
		if (activeAccount?.id) {
			getAccountFeed({
				id: activeAccount.id,
				limit: LIMIT_PAYLOAD,
				feedType: customFilters || [],
			});
		}
	}, [customFilters, updatePageFilters, activeAccount?.id, getAccountFeed]);

	const handleSearchClose = useCallback(() => {
		setSearchQuery('');
		setShowSearch(false);
		setCurrentLimit(LIMIT_PAYLOAD);
		if (activeAccount?.id) {
			getAccountFeed({
				id: activeAccount.id,
				limit: LIMIT_PAYLOAD,
				search: '',
				feedType: filter,
			});
		}
	}, [activeAccount?.id, getAccountFeed, filter]);

	const onShowSearchBar = useCallback(() => setShowSearch(true), []);

	useEffect(() => {
		if (!hasFetched.current && customerId && activeAccount?.id) {
			setCurrentLimit(LIMIT_PAYLOAD);
			const useCustomerId = activeAccount.id === customerId ? activeAccount.id : customerId;
			getAccountFeed({
				id: useCustomerId,
				limit: LIMIT_PAYLOAD,
				search: searchQuery,
				feedType: filter,
			});
			hasFetched.current = true;
		}
	}, [searchQuery, filter, getAccountFeed, activeAccount?.id, customerId]);

	const handleFilterChange = useCallback(
		(val: string[] | undefined) => {
			const value = val ?? [];
			setFilter(value);
			setCurrentLimit(LIMIT_PAYLOAD);
			if (value) {
				updatePageFilters(({eventFeed: value}));
			}
			if (activeAccount?.id) {
				getAccountFeed({
					id: activeAccount.id,
					limit: LIMIT_PAYLOAD,
					search: searchQuery,
					feedType: value,
				});
			}
		},
		[activeAccount?.id, getAccountFeed, searchQuery]
	);

	const handleScroll = useCallback(() => {
		const scrollElement = document.getElementById('events_wrapper');
		if (scrollElement) {
			const {scrollTop, scrollHeight, clientHeight} = scrollElement;
			if (
				scrollTop + clientHeight >= scrollHeight - 2 &&
				(accountFeed?.total_count ?? 0) > currentLimit &&
				!isFetchingRef.current &&
				activeAccount?.id
			) {
				isFetchingRef.current = true;
				const newLimit = currentLimit + LIMIT_PAYLOAD;
				setCurrentLimit(newLimit);
				getAccountFeed({
					id: activeAccount.id,
					limit: newLimit,
					search: searchQuery,
					feedType: filter,
					isLoading: true,
				}).finally(() => {
					isFetchingRef.current = false;
				});
			}
		}
	}, [
		accountFeed?.total_count,
		currentLimit,
		activeAccount?.id,
		filter,
		getAccountFeed,
		searchQuery,
	]);

	useEffect(() => {
		const scrollElement = document.getElementById('events_wrapper');
		if (scrollElement) {
			scrollElement.addEventListener('scroll', handleScroll);
		}
		return () => {
			if (scrollElement) {
				scrollElement.removeEventListener('scroll', handleScroll);
			}
		};
	}, [handleScroll]);

	useEffect(() => {
		if (customerId && activeAccount?.id) {
			hasFetched.current = customerId === activeAccount.id;
		}
	}, [customerId, activeAccount?.id, hasFetched]);

	useEffect(() => {
		if (!userConfig) {
			getUserConfig();
		}
	}, [userConfig, getUserConfig]);

	useEffect(() => {
		if (customerId) {
			setFilter(customFilters ?? []);
		}
	}, [customFilters, customerId]);

	return (
		<StyledGrowLayout
			direction="column"
			gap="none"
			nowrap
			fullHeight
			fullWidth
			style={{padding: '0px'}}
		>
			<Layout fullWidth alignItems="center" gap="xs" nowrap>
				<CommentEvent
					activeCustomer={activeAccount?.id ?? ''}
					filters={filter ?? []}
				/>
			</Layout>
			<Layout
				direction="row"
				nowrap
				justifyContent="space-between"
				fullWidth
				style={{height: '46px'}}
				alignItems="center"
			>
				<div style={{width: showSearch ? '100%' : 'auto'}} id="search_Events_Portal" />
				<Portal portalId="search_Events_Portal">
					<Layout
						nowrap
						alignItems="center"
						style={{width: showSearch ? '100%' : 'auto'}}
					>
						{
							showSearch ?
								(
									<>
										<SearchWidget
											onChange={handleSearch}
											placeholder={t('Search')}
											clearSearch={handleSearchClose}
											autoFocus
											value={searchQuery}
											fullWidth
										/>
										<div>
											<IconButton onClick={handleSearchClose}>
												<CloseIcon />
											</IconButton>
										</div>
									</>
								) :
								(
									<Button style={{paddingLeft: '0px'}} onClick={onShowSearchBar} icon={<SearchIcon />} compact>
										{t('Search')}
									</Button>
								)
						}
					</Layout>
				</Portal>
				{
					showSearch ? null : (
						<Layout alignItems="center" gap="xs" nowrap>
							{
								customFilters && filter.length !== customFilters.length && (
									<IconButton
										size="md"
										onClick={resetFilters}
									>
										<ReloadIcon />
									</IconButton>
								)
							}
							<Text variant="body1" color="textSecondary">{t('Type')}</Text>
							<Select
								value={filter}
								onChange={(val) => handleFilterChange(val as string[])}
								alt
								multiple
								placeholder={t('All')}
							>
								{/* <ListItem value="" primaryText={t('All')} /> */}
								<ListItem
									value="BN_CALL_LOG"
									primaryText={t('BN - Calls')}
									selected={filter.includes('BN_CALL_LOG')}
								/>
								<ListItem
									value="ERATE_CALL_LOG"
									primaryText={t('Erate - call')}
									selected={filter.includes('ERATE_CALL_LOG')}
								/>
								<ListItem
									value="ERATE_SYSTEM_SMS"
									primaryText={t('Erate - SMS')}
									selected={filter.includes('ERATE_SYSTEM_SMS')}
								/>
								<ListItem
									value="VISMA_INVOICE"
									primaryText={t('Visma invoice')}
									selected={filter.includes('VISMA_INVOICE')}
								/>
								<ListItem
									value="THREESIXTY_COMMENT"
									primaryText={t('360 - Comments')}
									selected={filter.includes('THREESIXTY_COMMENT')}
								/>
								<ListItem
									value="THREESIXTY_NEW_CUSTOMER_RESP"
									primaryText={t('360 - New customer responsible')}
									selected={filter.includes('THREESIXTY_NEW_CUSTOMER_RESP')}
								/>
								<ListItem
									value="THREESIXTY_NEW_SAF_VALUE"
									primaryText={t('360 - SAF')}
									selected={filter.includes('THREESIXTY_NEW_SAF_VALUE')}
								/>
								<ListItem
									value="THREESIXTY_OUTGOING_SMS"
									primaryText={t('360 - Outgoing SMS')}
									selected={filter.includes('THREESIXTY_OUTGOING_SMS')}
								/>
								<ListItem
									value="THREESIXTY_OUTGOING_TRUSTPILOT"
									primaryText={t('360 - Trustpilot')}
									selected={filter.includes('THREESIXTY_OUTGOING_TRUSTPILOT')}
								/>
								<ListItem
									value="THREESIXTY_INCIDENT_LOG"
									primaryText={t('360 - Incident log')}
									selected={filter.includes('THREESIXTY_INCIDENT_LOG')}
								/>
								<ListItem
									value="ZENDESK_UPSELL"
									primaryText={t('Zendesk - Upsell')}
									selected={filter.includes('ZENDESK_UPSELL')}
								/>
							</Select>
						</Layout>
					)
				}
			</Layout>
			<Divider style={{marginBottom: '15px'}}/>
			<Layout
				direction="row"
				fullHeight
				nowrap
				fullWidth
				justifyContent="space-between"
				style={{overflow: 'hidden'}}
			>
				<Events events={accountFeed} filters={filter} />
			</Layout>
		</StyledGrowLayout>
	);
}

export default CustomerEvents;
